/**
 * clasificadores/opiniones.js
 *
 * Clasificador del agente de Opiniones de productos (Mercado Libre).
 *
 * Recibe:
 *   { kbOpinionesItems, dashMetricas, publicaciones, config }
 *
 * Devuelve:
 *   {
 *     secciones: {
 *       mejorar_publicacion: [...items con hallazgos IA por publicación],
 *       atender_quejas_recurrentes: [...items con muchas quejas o rating bajo],
 *     },
 *     analisis: { ... }
 *   }
 *
 * Cada item de "mejorar_publicacion" representa UNA publicación con elogios
 * a destacar y/o quejas a contrarrestar. Cuando el agente corre en modo
 * "individual", crea 1 tarea por publicación.
 */

// Umbrales por defecto (configurables por empresa después).
const DEFAULTS = {
  ratingRiesgo: 3.0,           // rating ≤ esto → cuenta como "rating bajo"
  minReviewsParaQueja: 3,      // mínimo reviews para que un item entre en "atender quejas"
  scoreFichaUmbral: 70,        // score ≥ esto → la ficha ya refleja bien las opiniones
}

// `clustersConGap()` vivía acá y NO lo llamaba nadie (verificado con grep sobre
// todo el repo): la selección de gaps se hace inline abajo, partida por
// valencia. Se retira al escribir el test del archivo — código muerto que
// promete una fuente única de "qué es un gap" que en realidad no lo era.

export function clasificarOpiniones(datos) {
  // Ítem 16 — los docs sin clusters son registros de fallo del selector de
  // pendientes de la fase 2, no conocimiento: no cuentan ni como universo.
  const kbItems = (Array.isArray(datos.kbOpinionesItems) ? datos.kbOpinionesItems : [])
    .filter(kb => Array.isArray(kb?.clusters) && kb.clusters.length > 0)
  const dashMetricas = datos.dashMetricas || null
  const cfg = datos.config?.umbralesOpiniones?.ml || {}
  const RATING_RIESGO = typeof cfg.ratingRiesgo === 'number' ? cfg.ratingRiesgo : DEFAULTS.ratingRiesgo
  const MIN_REVIEWS = typeof cfg.minReviewsParaQueja === 'number' ? cfg.minReviewsParaQueja : DEFAULTS.minReviewsParaQueja
  const SCORE_OK = typeof cfg.scoreFichaUmbral === 'number' ? cfg.scoreFichaUmbral : DEFAULTS.scoreFichaUmbral

  const publicacionesMap = new Map()
  for (const p of (datos.publicaciones || [])) {
    publicacionesMap.set(p.id, {
      title: p.title || p.titulo,
      permalink: p.permalink,
      precio: p.price || p.precio || null,
    })
  }

  // ── Sección 1: Mejorar publicación ──
  // kbItems con al menos un hallazgo de IA (frase para destacar, refutación,
  // bullet, atributo faltante) que NO esté ya reflejado en la ficha.
  const mejorar = []
  for (const kb of kbItems) {
    const itemId = kb.itemId || (kb.id || '').split('_').slice(1).join('_')
    if (!itemId) continue

    const cs = Array.isArray(kb.clusters) ? kb.clusters : []
    const positivosConGap = cs.filter(c => c.valencia === 'positivo' && c.yaEnFicha !== true)
    const negativosConGap = cs.filter(c => c.valencia === 'negativo' && c.yaEnFicha !== true)
    const nFrases = Array.isArray(kb.frasesDestacadas) ? kb.frasesDestacadas.length : 0
    const nRefutaciones = Array.isArray(kb.refutaciones) ? kb.refutaciones.length : 0
    const nBullets = Array.isArray(kb.bulletsSugeridos) ? kb.bulletsSugeridos.length : 0
    const nAttrs = Array.isArray(kb.atributosFaltantes) ? kb.atributosFaltantes.length : 0

    const totalHallazgos =
      positivosConGap.length + negativosConGap.length + nFrases + nRefutaciones + nBullets + nAttrs
    if (totalHallazgos === 0) continue

    const score = kb.scoreCalidadFicha != null ? kb.scoreCalidadFicha : null
    const oportunidad = score != null ? Math.max(0, 100 - score) : null
    const pubMeta = publicacionesMap.get(itemId) || {}
    const tituloPub = kb.tituloItem || pubMeta.title || itemId
    const permalink = kb.permalinkItem || pubMeta.permalink || `https://articulo.mercadolibre.com.ar/${itemId}`
    const precio = kb.precioItem ?? pubMeta.precio ?? null

    mejorar.push({
      id: kb.id,
      _itemId: itemId,
      _docId: kb.id,
      _titulo: tituloPub,
      _permalink: permalink,
      hallazgos: {
        scoreCalidadFicha: score,
        scoreMotivo: kb.scoreMotivo || '',
        clustersPositivosConGap: positivosConGap.map(c => ({
          tema: c.temaCanonico,
          volumen: c.volumen,
          rateAvg: c.rateAvg,
          fraseDestacada: c.fraseDestacada || '',
        })),
        clustersNegativosConGap: negativosConGap.map(c => ({
          tema: c.temaCanonico,
          volumen: c.volumen,
          rateAvg: c.rateAvg,
          refutacionSugerida: c.refutacionSugerida || '',
        })),
        frasesDestacadas: kb.frasesDestacadas || [],
        refutaciones: kb.refutaciones || [],
        bulletsSugeridos: kb.bulletsSugeridos || [],
        atributosFaltantes: kb.atributosFaltantes || [],
        preguntasPrevistas: kb.preguntasPrevistas || [],
      },
      metricas: {
        totalReviews: kb.totalReviews || 0,
        ratingPromedio: kb.ratingPromedio ?? null,
        clustersPositivos: kb.clustersPositivos || 0,
        clustersNegativos: kb.clustersNegativos || 0,
        clustersConGap: kb.clustersConGap || 0,
        score,
        oportunidad,
        precio,
      },
      _resumen:
        `${kb.totalReviews || 0} reviews · ★ ${kb.ratingPromedio ?? '—'} · ` +
        `oportunidad ${oportunidad ?? '—'}/100 · ` +
        `👍 ${positivosConGap.length} a destacar · ⚠️ ${negativosConGap.length} a contrarrestar`,
    })
  }
  // Priorizar por oportunidad descendente (score más bajo primero), después por volumen.
  mejorar.sort((a, b) => {
    const oa = a.metricas.oportunidad ?? 0
    const ob = b.metricas.oportunidad ?? 0
    if (ob !== oa) return ob - oa
    return (b.metricas.totalReviews || 0) - (a.metricas.totalReviews || 0)
  })

  // ── Sección 2: Atender quejas recurrentes ──
  // Items con rating bajo y volumen suficiente para indicar problema real
  // (no solo opinión perdida). Toma del rollup si existe — si no, sintetiza
  // del kb propio.
  const quejas = []
  const yaIncluidos = new Set(mejorar.map(m => m._itemId))

  // Fuente A: dashMetricas.publicacionesEnRiesgo (si está disponible).
  const general = dashMetricas?.general || dashMetricas
  const enRiesgo = Array.isArray(general?.publicacionesEnRiesgo) ? general.publicacionesEnRiesgo : []
  for (const r of enRiesgo) {
    if (!r.itemId) continue
    if (yaIncluidos.has(r.itemId)) continue
    if ((r.total || 0) < MIN_REVIEWS) continue
    if (r.ratingAverage == null || r.ratingAverage > RATING_RIESGO) continue

    const pubMeta = publicacionesMap.get(r.itemId) || {}
    const tituloPub = r.titulo || pubMeta.title || r.itemId
    const permalink = r.permalink || pubMeta.permalink || `https://articulo.mercadolibre.com.ar/${r.itemId}`

    quejas.push({
      id: `riesgo_${r.itemId}`,
      _itemId: r.itemId,
      _docId: r.itemId,
      _titulo: tituloPub,
      _permalink: permalink,
      hallazgos: {
        ratingAverage: r.ratingAverage,
        totalReviews: r.total,
        origen: 'dashMetricas.publicacionesEnRiesgo',
      },
      metricas: {
        totalReviews: r.total,
        ratingPromedio: r.ratingAverage,
        gap: RATING_RIESGO - r.ratingAverage,
      },
      _resumen: `★ ${r.ratingAverage} · ${r.total} reviews · ${(RATING_RIESGO - r.ratingAverage).toFixed(1)} bajo el umbral`,
    })
  }

  // Fuente B (fallback): kbItems con rating bajo aunque no estén en dashMetricas.
  for (const kb of kbItems) {
    const itemId = kb.itemId
    if (!itemId) continue
    if (yaIncluidos.has(itemId)) continue
    if (quejas.some(q => q._itemId === itemId)) continue
    const total = kb.totalReviews || 0
    const rating = kb.ratingPromedio
    if (total < MIN_REVIEWS) continue
    if (rating == null || rating > RATING_RIESGO) continue

    const pubMeta = publicacionesMap.get(itemId) || {}
    const tituloPub = kb.tituloItem || pubMeta.title || itemId
    const permalink = kb.permalinkItem || pubMeta.permalink || `https://articulo.mercadolibre.com.ar/${itemId}`

    quejas.push({
      id: `riesgo_kb_${itemId}`,
      _itemId: itemId,
      _docId: kb.id,
      _titulo: tituloPub,
      _permalink: permalink,
      hallazgos: {
        ratingAverage: rating,
        totalReviews: total,
        origen: 'kbOpinionesItems',
        clustersNegativos: (kb.clusters || []).filter(c => c.valencia === 'negativo').map(c => ({
          tema: c.temaCanonico, volumen: c.volumen, rateAvg: c.rateAvg,
        })),
      },
      metricas: {
        totalReviews: total,
        ratingPromedio: rating,
        gap: RATING_RIESGO - rating,
      },
      _resumen: `★ ${rating} · ${total} reviews · ${(RATING_RIESGO - rating).toFixed(1)} bajo el umbral`,
    })
  }
  quejas.sort((a, b) => (b.metricas.gap || 0) - (a.metricas.gap || 0))

  return {
    secciones: {
      mejorar_publicacion: mejorar,
      atender_quejas_recurrentes: quejas,
    },
    analisis: {
      totalKbItems: kbItems.length,
      kbItemsConHallazgos: mejorar.length,
      itemsEnRiesgo: quejas.length,
      umbrales: { ratingRiesgo: RATING_RIESGO, minReviews: MIN_REVIEWS, scoreFichaUmbral: SCORE_OK },
    },
  }
}
